import axios from 'axios';

const baseURL = process.env.REACT_APP_API_BASE_URL || '/api';

/**
 * L'origine de l'API quand elle n'est pas la nôtre : en production l'interface est
 * hébergée chez LWS et l'API sur le VPS (`REACT_APP_API_BASE_URL=https://api-….../api`).
 * Vide quand l'API est servie sur la même origine (poste de travail, Traefik local).
 */
export const API_ORIGIN = /^https?:\/\//i.test(baseURL) ? new URL(baseURL).origin : '';

/**
 * Une adresse de fichier telle que l'API l'enregistre (`/api/media/<clé>`, relative à
 * ELLE), rendue absolue quand l'API vit sur une autre origine. Sans cela, la page hébergée
 * chez LWS demanderait la photo à LWS, où rien n'écoute. Toute autre adresse est rendue
 * telle quelle.
 */
export function assetUrl(u) {
  if (typeof u !== 'string' || !API_ORIGIN) return u;
  return u.startsWith('/api/') ? `${API_ORIGIN}${u}` : u;
}

/**
 * Trente secondes suffisent à toute requête ordinaire ; sans borne, un serveur
 * muet laissait un bouton « en cours » pour toujours. Les deux appels qui
 * attendent une réponse de Claude dans la requête elle-même (création manuelle
 * d'un client, test de connexion) reçoivent leur propre délai, plus long.
 *
 * `withCredentials` : le cookie de session est posé par l'API, sur SON domaine ; quand
 * l'interface vit ailleurs, le navigateur ne le joint aux appels que si on le lui demande.
 */
const http = axios.create({ baseURL, timeout: 30000, withCredentials: true });
const CLAUDE_TIMEOUT = 180000;

/**
 * Le jeton d'accès, gardé dans ce navigateur et présenté en en-tête à chaque appel.
 *
 * Le cookie posé par l'API ne suffit plus : l'interface (reload.binaire.tech) et l'API
 * (reload.binaire-backend.tech) sont deux sites différents, et un cookie de l'API y est
 * un cookie tiers — Safari et iOS le refusent, la connexion tournait en boucle. Le jeton
 * en en-tête passe partout. Il est stocké à la connexion, effacé à la déconnexion et
 * dès que l'API le refuse.
 */
const TOKEN_KEY = 'reload-access-token';
export function readAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function storeAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* stockage indisponible : le cookie fera ce qu'il peut */ }
}
http.interceptors.request.use((req) => {
  const token = readAccessToken();
  if (token && !req.headers.Authorization) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

/**
 * Un message d'erreur lisible, en français, quel que soit l'échec : le serveur
 * qui a répondu explique lui-même (`error`), et pour tout le reste on traduit le
 * code plutôt que de montrer « Network Error » ou « timeout of 30000ms exceeded ».
 */
function describe(err) {
  const server = err.response?.data?.error;
  if (server) return server;
  if (!err.response) {
    return err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
      ? 'Le serveur met trop de temps à répondre.'
      : 'Serveur injoignable.';
  }
  const { status } = err.response;
  if (status === 404) return 'Ressource introuvable.';
  if (status === 409) return 'Conflit : l\'opération n\'est plus possible dans cet état.';
  if (status === 413) return 'Contenu trop volumineux.';
  if (status >= 500) return 'Erreur interne du serveur.';
  return `Requête refusée (${status}).`;
}

http.interceptors.response.use(undefined, (err) => {
  err.message = describe(err);
  // Le serveur exige un jeton que ce navigateur n'a pas (ou plus) : la porte d'accès
  // se referme d'elle-même, quelle que soit la page qui a fait l'appel.
  if (err.response?.status === 401 && err.response?.data?.authRequired) {
    storeAccessToken('');
    window.dispatchEvent(new CustomEvent('reload:unauthorized'));
  }
  return Promise.reject(err);
});

export const api = {
  // Accès par jeton (facultatif côté serveur) : état, connexion, déconnexion.
  authStatus: () => http.get('/auth/status').then((r) => r.data),
  authLogin: (token) => http.post('/auth/login', { token }, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (r.data?.required) storeAccessToken(token); return r.data; }),
  authLogout: () => http.post('/auth/logout').then((r) => { storeAccessToken(''); return r.data; }),

  // Tableau de bord
  getDashboard: () => http.get('/dashboard').then((r) => r.data),

  // Clients
  listClients: () => http.get('/clients').then((r) => r.data),
  getClient: (id) => http.get(`/clients/${id}`).then((r) => r.data),
  createClient: (payload) =>
    http.post('/clients', payload, { timeout: CLAUDE_TIMEOUT }).then((r) => r.data),
  createClientFromUrl: (url) => http.post('/clients/from-url', { url }).then((r) => r.data),
  deleteClient: (id) => http.delete(`/clients/${id}`).then((r) => r.data),
  updateBrand: (id, brand) => http.patch(`/clients/${id}/brand`, brand).then((r) => r.data),
  harvestSite: (id) => http.post(`/clients/${id}/harvest`).then((r) => r.data),
  // Fusion de deux fiches : ce que chacune apporte, puis la fusion elle-même.
  mergePreview: (id, withId) => http.get(`/clients/${id}/merge-preview`, { params: { with: withId } }).then((r) => r.data),
  mergeClients: (id, sourceId, choices) => http.post(`/clients/${id}/merge`, { sourceId, choices }).then((r) => r.data),

  // Audit
  runAudit: (clientId) => http.post(`/clients/${clientId}/audit`).then((r) => r.data),
  listAudits: (clientId) => http.get(`/clients/${clientId}/audits`).then((r) => r.data),

  // Propositions
  generateProposal: (clientId) => http.post(`/clients/${clientId}/proposals`).then((r) => r.data),
  listProposals: (clientId) => http.get(`/clients/${clientId}/proposals`).then((r) => r.data),
  getProposal: (id) => http.get(`/proposals/${id}`).then((r) => r.data),
  generatePitch: (id) => http.post(`/proposals/${id}/pitch`).then((r) => r.data),
  generateQuote: (id) => http.post(`/proposals/${id}/quote`).then((r) => r.data),
  previewShare: (id, email) => http.get(`/proposals/${id}/share/preview`, { params: email ? { email } : {} }).then((r) => r.data),
  shareProposal: (id, email) => http.post(`/proposals/${id}/share`, { email }).then((r) => r.data),

  // Public
  getPublicProposal: (token) => http.get(`/public/proposals/${token}`).then((r) => r.data),
  // Le HTML d'une maquette se demande à part : les trois réunis alourdiraient
  // l'ouverture de la page pour deux documents que le client ne regarde pas encore.
  getPublicVariant: (token, index) =>
    http.get(`/public/proposals/${token}/variants/${index}`).then((r) => r.data),

  // L'ADRESSE du document d'une maquette, à charger dans une iframe ou un onglet.
  // Une maquette demande GSAP, ses plugins et ses polices en adresses relatives
  // (/api/vendor/…) : servie autrement que par une vraie adresse HTTP — depuis un
  // `blob:` par exemple — elle ne les obtient pas et reste parfaitement immobile.
  //
  // Pour la fiche interne, l'adresse est SIGNÉE par l'API : l'iframe et l'onglet ne
  // peuvent présenter ni en-tête ni, entre deux domaines, de cookie fiable.
  variantPreviewUrl: (proposalId, index) => http
    .get(`/proposals/${proposalId}/variants/${index}/preview-link`)
    .then((r) => `${baseURL}${r.data.path}`),
  publicVariantPreviewUrl: (token, index) => `${baseURL}/public/proposals/${token}/variants/${index}/preview`,
  // Le document lui-même, en texte, pour l'emporter : la liste des propositions
  // ne le transporte plus (trois maquettes pèsent plus que tout le reste réuni).
  getVariantDocument: (proposalId, index) =>
    http.get(`/proposals/${proposalId}/variants/${index}/preview`, {
      responseType: 'text',
      transformResponse: [(d) => d],
    }).then((r) => r.data),
  submitSelection: (token, payload) =>
    http.post(`/public/proposals/${token}/selection`, payload).then((r) => r.data),

  // Réglages
  getSettings: () => http.get('/settings').then((r) => r.data),
  testClaude: () => http.post('/settings/test', null, { timeout: CLAUDE_TIMEOUT }).then((r) => r.data),
  getPricing: () => http.get('/settings/pricing').then((r) => r.data),
  updatePricing: (pricing) => http.put('/settings/pricing', pricing).then((r) => r.data),
  getOutdatedQuotes: () => http.get('/settings/pricing/outdated').then((r) => r.data),
  recomputeQuotes: (proposalIds) =>
    http.post('/settings/pricing/recompute', { proposalIds }).then((r) => r.data),
  getJobRetention: () => http.get('/settings/jobs-retention').then((r) => r.data),
  updateJobRetention: (payload) => http.put('/settings/jobs-retention', payload).then((r) => r.data),
  runJobRetention: () => http.post('/settings/jobs-retention/run').then((r) => r.data),

  // Tâches en arrière-plan
  getJobs: (params = {}) => http.get('/jobs', { params }).then((r) => r.data),
  getJob: (id) => http.get(`/jobs/${id}`).then((r) => r.data),
  cancelJob: (id) => http.post(`/jobs/${id}/cancel`).then((r) => r.data),
  deleteJob: (id) => http.delete(`/jobs/${id}`).then((r) => r.data),
  purgeJobs: (params = {}) => http.delete('/jobs', { params }).then((r) => r.data),
};

export default api;
